import { useEffect, useState } from "react";
import { Check, Loader2, Eye, Lock } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LayoutGallery({ selectedId, onSelect }) {
  const [layouts, setLayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [allowed, setAllowed] = useState("all");

  useEffect(() => {
    Promise.all([api.get("/layouts"), api.get("/billing/me").catch(() => null)])
      .then(([l, b]) => {
        setLayouts(l.data);
        if (b?.data?.features?.allowed_layouts) setAllowed(b.data.features.allowed_layouts);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isLocked = (id) => allowed !== "all" && !allowed.includes(id);

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-400" data-testid="layout-gallery-loading">
        <Loader2 className="h-6 w-6 animate-spin inline" />
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="layout-gallery">
        {layouts.map((l) => {
          const active = selectedId === l.id;
          const locked = isLocked(l.id);
          return (
            <div
              key={l.id}
              data-testid={`layout-card-${l.id}`}
              className={`text-left rounded-2xl border bg-white overflow-hidden transition-all duration-200 hover:-translate-y-1 ${
                active ? "border-blue-500 ring-2 ring-blue-500/30 shadow-lg" : "border-slate-200 hover:border-blue-300 hover:shadow-md"
              }`}
            >
              <button
                type="button"
                onClick={() => setPreview(l)}
                data-testid={`layout-thumb-${l.id}`}
                className="relative block w-full h-52 overflow-hidden bg-slate-100 border-b border-slate-200 cursor-zoom-in"
                aria-label={`Preview ${l.name} layout`}
              >
                <iframe
                  title={l.name}
                  src={`${API}/layouts/${l.id}/preview.html`}
                  scrolling="no"
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ width: "1000px", height: "1500px", border: 0, transform: "scale(0.34)", transformOrigin: "top left", pointerEvents: "none" }}
                />
                {active && (
                  <div className="absolute top-3 right-3 h-7 w-7 rounded-full bg-blue-600 text-white flex items-center justify-center shadow" data-testid={`layout-selected-${l.id}`}>
                    <Check className="h-4 w-4" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-slate-900/0 hover:bg-slate-900/10 transition-colors">
                  <span className="opacity-0 hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-xs font-semibold text-slate-800 shadow">
                    <Eye className="h-3.5 w-3.5" /> Click to preview
                  </span>
                </div>
              </button>
              <div className="p-4">
                <div className="font-heading font-bold text-slate-900">{l.name}</div>
                <p className="text-sm text-slate-500 mt-1 leading-snug min-h-[40px]">{l.description}</p>
                <div className="flex items-center gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-200 text-slate-600"
                    onClick={() => setPreview(l)}
                    data-testid={`layout-preview-${l.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1.5" /> Preview
                  </Button>
                  <Button
                    size="sm"
                    disabled={locked}
                    className={`flex-1 disabled:opacity-100 ${locked ? "bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50 cursor-not-allowed" : `text-white ${active ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-900 hover:bg-slate-800"}`}`}
                    onClick={() => onSelect(l)}
                    data-testid={`layout-option-${l.id}`}
                  >
                    {locked ? <><Lock className="h-3.5 w-3.5 mr-1.5" /> Plus only</> : active ? "Selected" : "Use this layout"}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="layout-preview-dialog">
          <DialogHeader className="px-5 py-4 border-b border-slate-200">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-600" />
              {preview?.name} <span className="text-sm font-normal text-slate-400">— live preview</span>
            </DialogTitle>
          </DialogHeader>
          <div className="bg-slate-100 h-[68vh] overflow-auto">
            {preview && (
              <iframe
                title={`${preview.name} preview`}
                src={`${API}/layouts/${preview.id}/preview.html`}
                className="w-full h-full bg-white"
                style={{ border: 0 }}
                data-testid="layout-preview-frame"
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-200 bg-white">
            <p className="text-sm text-slate-500 hidden sm:block">{preview?.description}</p>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="outline" className="border-slate-200" onClick={() => setPreview(null)} data-testid="layout-preview-close">Close</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={preview && isLocked(preview.id)}
                onClick={() => { onSelect(preview); setPreview(null); }}
                data-testid="layout-preview-use"
              >
                {preview && isLocked(preview.id) ? <><Lock className="h-4 w-4 mr-1.5" /> Plus only</> : <><Check className="h-4 w-4 mr-1.5" /> Use this layout</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
